import React from 'react';
import { Modal, Text, BlockStack, Spinner, Card } from '@shopify/polaris';
import DOMPurify from 'dompurify';

function ArticlePreviewIdeasModal({ 
  open, 
  onClose, 
  article, 
  isGenerating, 
  onSaveAsDraft,
  isSaving 
}) {

  // Prevent modal crash when no article exists
  if (!article && !isGenerating) {
    return (
      <Modal open={open} onClose={onClose} title="Preview Generated Article">
        <Modal.Section>
          <Text>No article generated yet.</Text>
        </Modal.Section>
      </Modal>
    );
  }

  // Inject the CSS from your OptmisedContent example
  const modalContentStyles = `
    .Polaris-Modal-Dialog__Modal { max-width: 65% !important; }
    .generated-article-modal-content {
      line-height: 1.5;
      font-size: 14px;
      color: #2c2c2c;
    }
    .generated-article-modal-content h1{
      font-size: 28px;
      font-weight: 700;
      margin: 0 0 15px 0;
    }
    .generated-article-modal-content h2 {
      font-weight: 700;
      font-size: 1.45em;
      border-radius: 4px;
      margin-bottom: 12px;
    }
    .generated-article-modal-content h3 {
      font-weight: 600;
      font-size: 1.25em;
      margin: 0 0 12px 0;
    }
    .generated-article-modal-content p {
      margin: 0 0 15px 0;
    }
    .generated-article-modal-content ul {
      margin: 0px 0 0px 15px;
      padding-left: 20px;
      list-style-type: disc;
    }
    .generated-article-modal-content li { margin-bottom: 8px; }
    .generated-article-modal-content strong { font-weight: 700; }
    .generated-article-modal-content em { font-style: italic; }
    .generated-article-modal-content br.newline-block {
      display: block;
      margin-bottom: 12px;
    }
    .Polaris-Modal-Header__Title { font-size: 17px !important; }
    .generated-article-modal-content u { text-decoration: underline; }
    .generated-article-modal-content s, .generated-article-modal-content strike {
      text-decoration: line-through;
    }
    .generated-article-modal-content mark { background-color: #ffe066; }
  `;

  // Handle loading state gracefully
  if (isGenerating) {
    return (
      <Modal open={open} onClose={onClose} title="Generating Article">
        <Modal.Section>
          <BlockStack gap="400" align="center">
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <Spinner size="large" />
              <div style={{ marginTop: '16px' }}>
                <Text tone="subdued">Generating article content with AI...</Text>
              </div>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
    );
  }

  // Sanitize HTML
  const sanitizedContent = DOMPurify.sanitize(article?.content || '', {
    ALLOWED_TAGS: [
      "p","br","strong","em","ul","li","a","h1","h2","h3",
      "span","img","div","table","u","s","strike","mark"
    ],
    ALLOWED_ATTR: [
      "href","src","alt","width","height","style","class","data-contrast"
    ]
  });

  const formattedContent = sanitizedContent.replace(
    /\n\n\n\n/g,
    '<br class="newline-block">'
  );

  return (
    <>
      <style>{modalContentStyles}</style>

      <Modal
        open={open}
        onClose={onClose}
        title="Generated Article Preview"
        primaryAction={{
          content: isSaving ? "Saving..." : 'Save as Draft',
          onAction: onSaveAsDraft,
          loading: isSaving,
          disabled: isGenerating || !article,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: onClose }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            
            {/* Title */}
            <Text variant="headingLg" as="h2">
              {article.title || "Untitled Article"}
            </Text>

            {/* New Metadata Fields (Descriptive & Read Time) */}
            {article.descriptive && (
              <Text variant="bodyMd" tone="subdued">
                {article.descriptive}
              </Text>
            )}

            {article.readtime && (
              <Text variant="bodySm" tone="subdued">
                {article.readtime}
              </Text>
            )}

            {/* Content with HTML */}
            <div
              className="generated-article-modal-content"
              style={{
                border: '1px solid #e1e3e5',
                borderRadius: '8px',
                padding: '16px',
                maxHeight: '500px',
                overflowY: 'auto',
                backgroundColor: "#ffffff"
              }}
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />

            {/* BlogHero Promo Block */}
            <div style={{ marginTop: "12px" }}>
              <Card background="bg-surface-brand-selected">
                <BlockStack gap="200">
                  <Text variant="bodyMd">
                    ✨ For better editing options and advanced AI rewriting features, try BlogHero.{" "}
                    <a 
                      href="https://apps.shopify.com/bloghero"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 600, color: "#006fbb" }}
                    >
                      BlogHero
                    </a>.
                  </Text>
                </BlockStack>
              </Card>
            </div>

            <Text variant="bodySm" tone="subdued">
              You can edit this article after saving it as a draft.
            </Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
}

export default ArticlePreviewIdeasModal;
